import { motion } from "framer-motion";
import { Film } from "lucide-react";
import CalendarSpread from "./CalendarSpread";
import SportPosters from "./SportPosters";
import TypographyWorks from "./TypographyWorks";
import { useLightbox } from "./LightboxContext";

const KLEIN = "#0851F2";

// Decorative Klein-blue shapes scattered behind every sub-section of Graphic Design.
function GraphicBackdropShapes() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none overflow-hidden z-0"
    >
      {/* Klein-blue backdrop shapes — pinned to L/R margins for visibility */}
      {/* LEFT margin */}
        <svg
          className="absolute"
          style={{ left: "-100px", top: "8%", width: "300px", height: "300px", opacity: 0.22 }}
          viewBox="0 0 300 300"
          fill="none"
        >
          <circle cx="150" cy="150" r="140" stroke={KLEIN} strokeWidth="3" />
        </svg>
        <span
          className="absolute"
          style={{
            left: "1%",
            top: "32%",
            width: "120px",
            height: "120px",
            background: KLEIN,
            opacity: 0.14,
            transform: "rotate(-14deg)",
          }}
        />
        <span
          className="absolute"
          style={{
            left: "2%",
            top: "56%",
            width: "32px",
            height: "32px",
            background:
              `linear-gradient(${KLEIN},${KLEIN}) center/100% 4px no-repeat,` +
              `linear-gradient(${KLEIN},${KLEIN}) center/4px 100% no-repeat`,
            opacity: 0.6,
          }}
        />
        <svg
          className="absolute"
          style={{ left: "1%", bottom: "10%", width: "180px", height: "160px", opacity: 0.3 }}
          viewBox="0 0 180 160"
          fill="none"
        >
          <polygon points="90,10 170,150 10,150" stroke={KLEIN} strokeWidth="3" />
        </svg>

        {/* RIGHT margin */}
        <svg
          className="absolute"
          style={{ right: "1%", top: "12%", width: "260px", height: "60px", opacity: 0.4 }}
          viewBox="0 0 260 60"
          fill="none"
        >
          <path
            d="M 5 30 Q 35 5, 65 30 T 125 30 T 185 30 T 250 30"
            stroke={KLEIN}
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
        <span
          className="absolute"
          style={{
            right: "3%",
            top: "26%",
            width: "150px",
            height: "150px",
            background: KLEIN,
            opacity: 0.1,
            transform: "rotate(18deg)",
          }}
        />
        <span
          className="absolute"
          style={{
            right: "2%",
            top: "50%",
            width: "160px",
            height: "230px",
            border: `2.5px solid ${KLEIN}`,
            opacity: 0.24,
            transform: "rotate(-6deg)",
          }}
        />
        <svg
          className="absolute"
          style={{ right: "-60px", bottom: "5%", width: "280px", height: "280px", opacity: 0.22 }}
          viewBox="0 0 280 280"
          fill="none"
        >
          <circle cx="140" cy="140" r="130" stroke={KLEIN} strokeWidth="2.5" />
          <circle cx="140" cy="140" r="84" stroke={KLEIN} strokeWidth="2.5" />
          <circle cx="140" cy="140" r="40" stroke={KLEIN} strokeWidth="2.5" />
        </svg>

        {/* Vertical dot rails — left + right */}
        {[
          { side: "left", top: "82%" },
          { side: "left", top: "85%" },
          { side: "left", top: "88%" },
          { side: "right", top: "5%" },
          { side: "right", top: "8%" },
          { side: "right", top: "11%" },
        ].map((d, i) => (
          <span
            key={`gd-dot-${i}`}
            className="absolute"
            style={{
              [d.side]: "4%",
              top: d.top,
              width: "9px",
              height: "9px",
              borderRadius: "9999px",
              background: KLEIN,
              opacity: 0.55,
            }}
          />
        ))}
    </div>
  );
}

const moviePosters = [
  {
    title: "Taxi Driver",
    year: "2025",
    note: "Double-exposure key art — silhouette × Times Square taxis. A grief-noir homage.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/yt540q0w_taxi.jpg",
  },
  {
    title: "Oppenheimer",
    year: "2025",
    note: "A man, a flame, a cloud. Hat and embers carrying the weight of the world.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/abcedaqh_image.png",
  },
  {
    title: "Frozen Echoes",
    year: "2025",
    note: "Original concept — a face behind frosted glass, blue tears the only color.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/xpo7ad5h_image.png",
  },
];

export default function GraphicDesign() {
  const { open } = useLightbox();
  return (
    <section
      id="graphic"
      className="relative py-24 md:py-40 border-t border-[var(--ink)] bg-white overflow-hidden"
      data-testid="graphic-design-section"
    >
      {/* Klein-blue decorative shapes scattered behind every subsection */}
      <GraphicBackdropShapes />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="md:col-span-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              §03 — Graphic Design
            </span>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-sans-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.03em] uppercase">
              <span style={{ color: "#0851F2" }}>Graphic</span>
              <br />
              <span
                className="font-display italic lowercase font-light"
                style={{ color: "#0851F2" }}
              >
                selected works
              </span>
            </h2>
            <p className="mt-6 text-lg md:text-xl text-[var(--ink-soft)] max-w-[52ch] leading-relaxed">
              Posters, calendars and editorial pieces — the analog side of the
              practice. Concept key art studies, type-led layouts and the
              occasional double-exposure indulgence.
            </p>
          </div>
        </div>

        {/* Sport edits — Basketball first */}
        <SportPosters />

        {/* Calendar interactive spread */}
        <CalendarSpread />

        {/* Movie Posters — featured */}
        <div className="mb-20 md:mb-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
            <div className="flex items-center gap-4">
              <span className="w-10 h-10 border border-[var(--ink)] flex items-center justify-center">
                <Film className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <h3 className="font-sans-display text-3xl md:text-5xl tracking-[-0.02em] font-light uppercase">
                Movie posters
              </h3>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              §03.c — Key art studies
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {moviePosters.map((p, i) => (
              <motion.figure
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="group"
                data-testid={`movie-poster-${p.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div
                  className="relative aspect-[2/3] w-full overflow-hidden bg-[var(--ink)] cursor-zoom-in"
                  onClick={() => open(p.img, `${p.title} — movie poster design`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      open(p.img, `${p.title} — movie poster design`);
                    }
                  }}
                >
                  <img
                    src={p.img}
                    alt={`${p.title} — movie poster design`}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/85 bg-black/45 backdrop-blur-sm px-2.5 py-1">
                    Poster · {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <figcaption className="mt-5 flex items-start justify-between gap-6">
                  <div>
                    <div className="font-display italic text-2xl md:text-3xl leading-tight">
                      {p.title}
                    </div>
                    <p className="mt-2 text-sm md:text-base text-[var(--ink-soft)] leading-relaxed max-w-[36ch]">
                      {p.note}
                    </p>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-soft)] whitespace-nowrap pt-2">
                    {p.year}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>

        {/* Typography works */}
        <TypographyWorks />
      </div>
    </section>
  );
}
